/**
 * DIVETTA MILANO R7 — shared capability, lifecycle and RAF coordinator.
 * Route owners opt in explicitly with data-r7-scene or data-zorge-scene.
 * Read/write phase adaptation: REPO-CAP-1028 createRenderStep.
 */
(() => {
  'use strict';

  const KEY = '__DivettaR7Zorge';
  window[KEY]?.destroy?.();

  const controller = new AbortController();
  const { signal } = controller;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  const subscribers = new Map();
  const sceneRecords = new Map();
  const nearbyScenes = new Set();
  let activeScene = null;
  let editorScene = null;
  let frame = 0;
  let resizeObserver = null;
  let sceneObserver = null;
  let previousScrollY = window.scrollY;
  let direction = 1;
  let disposed = false;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = (value) => {
    const t = clamp(value);
    return t * t * (3 - (2 * t));
  };
  const body = () => document.body;
  const route = () => body()?.dataset.r7Route || body()?.dataset.pageType || 'utility';

  const capabilityState = () => {
    const memory = Number(navigator.deviceMemory || 0);
    const processors = Number(navigator.hardwareConcurrency || 0);
    const saveData = Boolean(connection?.saveData);
    const lowMemory = memory > 0 && memory <= 4;
    const lowCpu = processors > 0 && processors <= 4;
    const lowCapability = lowMemory || lowCpu;
    const canAnimate = !reduceMotion.matches && !saveData && !lowCapability;
    return {
      canAnimate,
      canAutoplay: canAnimate,
      reducedMotion: reduceMotion.matches,
      saveData,
      lowCapability,
      memory,
      processors
    };
  };

  const isProtectedNode = (node) => Boolean(
    node.closest?.('[data-ldm-protected="true"], [data-ldm-ignore="true"], [data-r7-zorge-ignore]')
    || node.matches?.('script, style, noscript, template')
  );

  const configureMode = () => {
    const root = document.documentElement;
    const capabilities = capabilityState();
    root.classList.toggle('r7-zorge-enhanced', capabilities.canAnimate);
    root.classList.toggle('r7-zorge-reduced', capabilities.reducedMotion);
    root.classList.toggle('r7-zorge-low-data', capabilities.saveData);
    root.classList.toggle('r7-zorge-low-capability', capabilities.lowCapability);
    body()?.setAttribute('data-r7-capability-tier', capabilities.canAnimate ? 'enhanced' : 'static');
  };

  const discoverScenes = () => {
    const main = document.getElementById('MainContent');
    if (!main || typeof main.querySelectorAll !== 'function') return [];
    const seen = new Set();
    return Array.from(main.querySelectorAll('[data-r7-scene], [data-zorge-scene]')).filter((node) => {
      if (!(node instanceof HTMLElement) || seen.has(node) || !node.isConnected || isProtectedNode(node)) return false;
      seen.add(node);
      return true;
    });
  };

  const clearScenes = () => {
    sceneObserver?.disconnect();
    sceneObserver = null;
    nearbyScenes.clear();
    sceneRecords.forEach((_record, node) => {
      node.classList.remove('r7-zorge-scene', 'is-r7-zorge-active', 'is-r7-zorge-before', 'is-r7-zorge-after');
      node.style.removeProperty('--r7-scene-progress');
      node.style.removeProperty('--r7-scene-presence');
      node.style.removeProperty('--r7-scene-direction');
    });
    sceneRecords.clear();
    activeScene = null;
  };

  const registerScenes = (nodes) => {
    nodes.forEach((node, index) => {
      node.classList.add('r7-zorge-scene');
      sceneRecords.set(node, {
        index,
        id: node.dataset.r7Scene || node.dataset.zorgeScene || `scene-${index + 1}`
      });
    });
  };

  const observeScenes = (nodes) => {
    if (!('IntersectionObserver' in window)) {
      nodes.forEach((node) => nearbyScenes.add(node));
      return;
    }
    sceneObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) nearbyScenes.add(entry.target);
        else nearbyScenes.delete(entry.target);
      });
      requestFrame();
    }, { rootMargin: '100% 0px 100% 0px', threshold: 0 });
    nodes.forEach((node) => sceneObserver.observe(node));
  };

  const refresh = () => {
    clearScenes();
    configureMode();
    const nodes = discoverScenes();
    registerScenes(nodes);
    observeScenes(nodes);
    requestFrame();
  };

  const frameProgress = (node, viewport) => {
    const rect = node.getBoundingClientRect();
    const travel = Math.max(rect.height + (viewport * .68), 1);
    const progress = clamp(((viewport * .88) - rect.top) / travel);
    const center = rect.top + (rect.height / 2);
    const distance = Math.abs(center - (viewport * .5));
    const presenceRange = Math.max(viewport * .88, rect.height * .56);
    return { progress, presence: smooth(1 - clamp(distance / presenceRange)), distance };
  };

  const updateScenes = () => {
    const viewport = Math.max(window.innerHeight, 1);
    const currentScrollY = window.scrollY;
    direction = currentScrollY === previousScrollY ? direction : currentScrollY > previousScrollY ? 1 : -1;
    previousScrollY = currentScrollY;

    const connected = Array.from(sceneRecords.keys()).filter((node) => node.isConnected);
    const candidates = connected.filter((node) => nearbyScenes.has(node) || node === editorScene);
    if (!candidates.length) candidates.push(...connected);
    const values = new Map();
    const editorOwnsScene = Boolean(editorScene && sceneRecords.has(editorScene));
    let nearest = editorOwnsScene ? editorScene : null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    candidates.forEach((node) => {
      const computed = frameProgress(node, viewport);
      values.set(node, computed);
      if (!editorOwnsScene && computed.distance < nearestDistance) {
        nearest = node;
        nearestDistance = computed.distance;
      }
    });

    const activeIndex = nearest ? sceneRecords.get(nearest)?.index ?? 0 : 0;
    connected.forEach((node) => {
      const record = sceneRecords.get(node);
      const computed = values.get(node) || { progress: record.index < activeIndex ? 1 : 0, presence: 0 };
      const put = (key, value) => { if (node.style.getPropertyValue(key) !== value) node.style.setProperty(key, value); };
      put('--r7-scene-progress', computed.progress.toFixed(4));
      put('--r7-scene-presence', (editorScene === node ? 1 : computed.presence).toFixed(4));
      put('--r7-scene-direction', String(direction));
      node.classList.toggle('is-r7-zorge-active', node === nearest);
      node.classList.toggle('is-r7-zorge-before', record.index < activeIndex);
      node.classList.toggle('is-r7-zorge-after', record.index > activeIndex);
    });

    if (nearest && nearest !== activeScene) {
      activeScene = nearest;
      const record = sceneRecords.get(nearest);
      document.dispatchEvent(new CustomEvent('r7:zorge:scenechange', {
        detail: { scene: nearest, index: record?.index ?? 0, id: record?.id || '', direction }
      }));
    }
  };

  const tick = (time) => {
    frame = 0;
    if (disposed || document.hidden) return;
    updateScenes();
    const detail = { time, scrollY: window.scrollY, direction, route: route(), desktop: desktop.matches, ...capabilityState() };
    subscribers.forEach((callback) => {
      try { callback(detail); } catch (error) { console.error('[Divetta coordinator]', error); }
    });
    document.dispatchEvent(new CustomEvent('r7:zorge:frame', { detail }));
  };

  const requestFrame = () => {
    if (!disposed && !document.hidden && !frame) frame = window.requestAnimationFrame(tick);
  };

  const subscribe = (name, callback) => {
    if (!name || typeof callback !== 'function') return () => {};
    subscribers.set(name, callback);
    requestFrame();
    return () => { if (subscribers.get(name) === callback) subscribers.delete(name); };
  };

  const sceneFromEditorTarget = (target) => target instanceof Element
    ? target.closest('[data-r7-scene], [data-zorge-scene]') || target.querySelector('[data-r7-scene], [data-zorge-scene]')
    : null;

  const onEditorSelect = (event) => {
    const scene = sceneFromEditorTarget(event.target);
    if (!scene) return;
    if (!sceneRecords.has(scene)) refresh();
    editorScene = sceneRecords.has(scene) ? scene : null;
    requestFrame();
  };

  const onEditorDeselect = () => {
    editorScene = null;
    requestFrame();
  };

  const syncMenuState = (details) => {
    if (!(details instanceof HTMLDetailsElement) || details.id !== 'Details-menu-drawer-container') return;
    body()?.classList.toggle('r7-zorge-menu-open', details.open);
  };

  const resize = () => refresh();
  window.addEventListener('scroll', requestFrame, { passive: true, signal });
  window.addEventListener('resize', resize, { passive: true, signal });
  window.addEventListener('pageshow', refresh, { signal });
  document.addEventListener('visibilitychange', () => { if (document.hidden) { cancelAnimationFrame(frame); frame = 0; } else refresh(); }, { signal });
  desktop.addEventListener?.('change', resize, { signal });
  reduceMotion.addEventListener?.('change', resize, { signal });
  connection?.addEventListener?.('change', resize, { signal });
  document.addEventListener('toggle', (event) => syncMenuState(event.target), { capture: true, signal });
  document.addEventListener('shopify:section:load', refresh, { signal });
  document.addEventListener('shopify:section:unload', () => window.requestAnimationFrame(refresh), { signal });
  document.addEventListener('shopify:section:reorder', () => window.requestAnimationFrame(refresh), { signal });
  document.addEventListener('shopify:section:select', onEditorSelect, { signal });
  document.addEventListener('shopify:section:deselect', onEditorDeselect, { signal });
  document.addEventListener('shopify:block:select', onEditorSelect, { signal });
  document.addEventListener('shopify:block:deselect', onEditorDeselect, { signal });

  const mount = () => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    document.documentElement.classList.add('r7-zorge-available');
    refresh();
    const main = document.getElementById('MainContent');
    if ('ResizeObserver' in window && main) {
      resizeObserver = new ResizeObserver(requestFrame);
      resizeObserver.observe(main);
    }
    requestFrame();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true, signal });
  else mount();

  window[KEY] = window.DivettaZorge = {
    mount,
    refresh,
    requestFrame,
    subscribe,
    get capabilities() { return capabilityState(); },
    get canAnimate() { return capabilityState().canAnimate; },
    get canAutoplay() { return capabilityState().canAutoplay; },
    get activeScene() { return activeScene; },
    get isDesktop() { return desktop.matches; },
    get reducedMotion() { return reduceMotion.matches; },
    destroy() {
      disposed = true;
      controller.abort();
      resizeObserver?.disconnect();
      sceneObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      clearScenes();
      subscribers.clear();
      document.documentElement.classList.remove('r7-zorge-available', 'r7-zorge-enhanced', 'r7-zorge-reduced', 'r7-zorge-low-data', 'r7-zorge-low-capability');
      body()?.classList.remove('r7-zorge-menu-open');
      body()?.removeAttribute('data-r7-capability-tier');
      delete window[KEY];
      delete window.DivettaZorge;
    }
  };
})();
